import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ModuleService } from '@/services/ModuleService';
import type { IntroStatus } from '@/lib/models/module';

interface ModuleProgressContextValue {
    introStatus: IntroStatus;
    isLoadingIntro: boolean;
    refreshIntroStatus: () => Promise<void>;
}

const ModuleProgressContext = createContext<ModuleProgressContextValue>({
    introStatus: { intro_completed: false },
    isLoadingIntro: true,
    refreshIntroStatus: async () => {},
});

export const useModuleProgress = () => useContext(ModuleProgressContext);

export const ModuleProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [introStatus, setIntroStatus] = useState<IntroStatus>({ intro_completed: false });
    const [isLoadingIntro, setIsLoadingIntro] = useState(true);

    const refreshIntroStatus = useCallback(async () => {
        if (!user || user.role !== 'candidate') {
            setIntroStatus({ intro_completed: true }); // non-candidates bypass intro
            setIsLoadingIntro(false);
            return;
        }

        try {
            const status = await ModuleService.getIntroStatus();
            setIntroStatus(status);
        } catch (error) {
            console.error('Error fetching intro status:', error);
        } finally {
            setIsLoadingIntro(false);
        }
    }, [user]);

    useEffect(() => {
        refreshIntroStatus();
    }, [refreshIntroStatus]);

    return (
        <ModuleProgressContext.Provider value={{ introStatus, isLoadingIntro, refreshIntroStatus }}>
            {children}
        </ModuleProgressContext.Provider>
    );
};
